import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Decoration } from '@atlaskit/editor-prosemirror/view';
import type { Node as PMNode, MarkType } from '@atlaskit/editor-prosemirror/model';
import { DOMSerializer, Fragment } from '@atlaskit/editor-prosemirror/model';
import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import { token } from '@atlaskit/tokens';

import type { IdentifiableConflictChange } from './main';

// Apply strike mark to deleted text
function applyMarkToFragment(fragment: Fragment, markType: MarkType | undefined) {
	if (!markType) {
		return fragment;
	}
	// Create a mark of the given type
	const mark = markType.create({});
	const nodes: PMNode[] = [];
	fragment.forEach((node) => {
		if (node.isText) {
			nodes.push(node.mark([...node.marks, mark]));
		} else {
			nodes.push(node.copy(applyMarkToFragment(node.content, markType)));
		}
	});
	// Map over each node in the fragment and apply the mark
	return Fragment.fromArray(nodes);
}

export const createDeletedDecoration = (
	conflict: IdentifiableConflictChange,
	tr: Transaction,
	selected: boolean = false,
): Decoration | undefined => {
	return Decoration.widget(
		conflict.from,
		() => {
			const strikenText = document.createElement('span');
			const style = convertToInlineCss({
				background: selected
					? token('color.background.accent.orange.subtlest.hovered')
					: token('color.background.accent.orange.subtlest'),
				textDecoration: 'underline',
				textDecorationStyle: 'dotted',
				textDecorationThickness: token('space.025'),
				textDecorationColor: token('color.border.accent.orange'),
			});
			strikenText.setAttribute('style', style);

			// Render striken text fragment
			strikenText.appendChild(
				DOMSerializer.fromSchema(tr.doc.type.schema).serializeFragment(
					applyMarkToFragment(conflict?.local.content, tr.doc.type.schema.marks.strike),
				),
			);
			return strikenText;
		},
		{
			side: -1,
			ignoreSelection: false,
		},
	);
};
